import {
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Link } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { SignalRContext } from '../../../../Context/SignalR.context';
import { getCommitteeLabel } from '../../../../models/committees.types';
import { Notification, NotificationTypeId } from '../../../../models/notification.types';
import { defaultPageInfo, IPageInfo } from '../../../../models/page.types';
import { CallbackMethodNames } from '../../../../models/signalR.types';
import { notificationsActions } from '../../../../reducers/notificationsReducer/notificationsSlice';
import format from 'date-fns/format';
import {
  deleteNotification,
  getNotifications,
  updateNotificationAsRead,
} from '../../../../services/notification/notifications.service';
import { downloadImportReport as downloadImportReport } from '../../../../services/users/users.service';
import { RootState } from '../../../../store';
import { formatDate, timeAgo } from '../../../../utils/dateFunctionUtils';
import { getOnInfiniteScroll } from '../../../../utils/infiniteScrolling';
import { Paths, getExamineeListUrl } from '../../../../utils/routerUrls';
import IconWithDot from '../../../IconWithDot/IconWithDot.component';
import InfoShield from '../../../InfoShield.component';
import { useStyles as useNavBarStyles } from '../Navbar.styles';
import { useStyles } from './NotificationsMenu.styles';
import { downloadExamResultsReport, downloadSioePkzExportReport } from '../../../../services/results/results.service';
import TooltipOne from '../../../TooltipOne/TooltipOne.component';
import {
  downloadDeleteTasksSolutionSheetReport,
  downloadExamineeImportReport,
} from '../../../../services/images/images.service';
import { SystemId } from '../../../../services/images/images.types';
import { Path } from 'fabric/fabric-impl';
import { fill } from '../../../../utils/contrastSvgAttributes';
import { useTranslation } from 'react-i18next';

const SETTING_AS_READ_TIMEAOUT_DURATION = 3000;

const NotificationsMenu: React.FC = () => {
  const navBarStyles = useNavBarStyles();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const anyUnreadNotificationsFound = useSelector(
    (state: RootState) => state.notifications.anyUnreadNotificationsFound,
  );

  const { openAlert, showLoader } = useLayoutContext();

  const isMenuActivated = useRef<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const [activeNotificationId, setActiveNotificationId] = useState<string | null>(null);

  const [, setSettingAsReadTimeout] = useState<NodeJS.Timeout | null>(null);

  const cancelSettingAsReadTimeout = () => {
    setSettingAsReadTimeout((prevState) => {
      if (prevState) {
        clearTimeout(prevState);
      }
      return null;
    });
  };

  const loadNotifications = (overrideNotifications: boolean): Promise<undefined> => {
    return new Promise((resolve) => {
      getNotifications(pageInfo.pageSize, pageInfo.currentPage).then((response) => {
        resolve(undefined);
        if (overrideNotifications) {
          setNotifications(response.data.notifications);
        } else {
          setNotifications((prevState) => [...prevState, ...response.data.notifications]);
        }
        setPageInfo(response.data.pageInfo);

        const unReadNotificationsIds: string[] = response.data.notifications
          .filter((item) => !item.isRead)
          .map((item) => item.id);

        if (unReadNotificationsIds.length) {
          const timeoutId = setTimeout(() => {
            updateNotificationAsRead(unReadNotificationsIds).then(
              (response) => {
                setNotifications((prevState) =>
                  prevState.map((item) =>
                    unReadNotificationsIds.includes(item.id) ? { ...item, isRead: true } : item,
                  ),
                );
                if (response.data === 0) {
                  dispatch(notificationsActions.updateAnyUnreadNotificationsFound(false));
                }
              },
              (e) => console.error(e),
            );
          }, SETTING_AS_READ_TIMEAOUT_DURATION);
          setSettingAsReadTimeout(timeoutId);
        }
      });
    });
  };

  SignalRContext.useSignalREffect(
    CallbackMethodNames.NewPushNotification,
    () => dispatch(notificationsActions.updateAnyUnreadNotificationsFound(true)),
    [],
  );
  SignalRContext.useSignalREffect(
    CallbackMethodNames.UserDeletedInfo,
    () => dispatch(notificationsActions.updateAnyUnreadNotificationsFound(true)),
    [],
  );

  SignalRContext.useSignalREffect(
    CallbackMethodNames.NewProcedureNotification,
    () => dispatch(notificationsActions.updateAnyUnreadNotificationsFound(true)),
    [],
  );

  SignalRContext.useSignalREffect(
    CallbackMethodNames.DeleteTasksSolutionSheetsMessage,
    () => dispatch(notificationsActions.updateAnyUnreadNotificationsFound(true)),
    [],
  );

  useEffect(() => {
    if (!anchorEl) {
      cancelSettingAsReadTimeout();
    }
  }, [anchorEl]);

  useEffect(() => {
    if (isMenuActivated.current) {
      loadNotifications(false);
    }
  }, [pageInfo.currentPage, isMenuActivated]);

  useEffect(() => {
    return () => {
      cancelSettingAsReadTimeout();
    };
  }, []);

  const handleToggleNotificationsList = (event: React.MouseEvent<HTMLButtonElement>) => {
    isMenuActivated.current = true;
    const target = event.currentTarget;
    loadNotifications(true).then(() => {
      setAnchorEl(target);
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
    isMenuActivated.current = false;
    setPageInfo(defaultPageInfo);
  };

  const handleNotificationClick = (type: NotificationTypeId) => {
    if (type === NotificationTypeId.NewMessage) history.push(Paths.mailbox);
  };

  const handleDeleteNotification = (event: React.MouseEvent<HTMLButtonElement>, notificationId: string) => {
    event.stopPropagation();
    deleteNotification(notificationId).then(
      () => {
        setNotifications((prevState) => prevState.filter((item) => item.id !== notificationId));
      },
      () => openAlert('delete_error'),
    );
  };

  const handleOnScroll = useCallback(
    (endReached: boolean) => {
      if (endReached && pageInfo.hasNext) {
        setPageInfo((prevState) => {
          return { ...prevState, currentPage: prevState.currentPage + 1 };
        });
      }
    },
    [pageInfo.hasNext],
  );

  const getNotificationIcon = (type: NotificationTypeId): JSX.Element => {
    switch (type) {
      case NotificationTypeId.NewMessage:
        return <MailOutlineIcon />;
      case NotificationTypeId.ExaminerImportSuccess:
      case NotificationTypeId.ExaminerImportFailed:
      case NotificationTypeId.ExaminerBlocked:
      case NotificationTypeId.ExportResultSuccess:
      case NotificationTypeId.ProcedureMessage:
      case NotificationTypeId.ReportFinished:
      case NotificationTypeId.ReportFailed:
        return <NotificationsNoneIcon />;
      default:
        return <NotificationsNoneIcon />;
    }
  };

  function isJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  const getNotificationText = (notification: Notification) => {
    const parsedNotificationContent = isJsonString(notification.content)
      ? JSON.parse(notification.content)
      : notification.content;

    const systemType = parsedNotificationContent ? parsedNotificationContent.SourceType : '';

    const getSystemName = () => {
      switch (systemType) {
        case SystemId.Sioeo:
          return 'SIOEO';
        case SystemId.Sioepkz:
          return 'SIOEPKZ';
        default:
          return t('global:shortNames.system');
      }
    };

    const handleDownloadExamResultsReport = () => {
      showLoader(true);
      downloadExamResultsReport(JSON.parse(notification.content).ReportJobId, JSON.parse(notification.content).FileName)
        .then(
          () => {
            openAlert('success');
          },
          () => {
            openAlert('error');
          },
        )
        .finally(() => showLoader(false));
    };

    const handleDeleteUserResult = (succeeded: boolean) => {
      return succeeded ? 'Poprawnie usunięto konto uzytkownika' : 'Nie udało się usunąć konta użytkownika';
    };
    switch (notification.notificationType) {
      case NotificationTypeId.NewMessage:
        return (
          <>
            <div>Masz nową wiadomość</div>
            <div>{`Od: ${notification.from}`}</div>
            <div
              style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >{`Temat: ${notification.content}`}</div>
          </>
        );
      case NotificationTypeId.ExaminerImportSuccess:
        return (
          <>
            <div>Import egzaminatorów z {notification.from.toUpperCase()}:</div>
            <div>Zakończono import egzaminatorów</div>
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={async () => {
                  await downloadImportReport(
                    notification.content,
                    `Import_Egzaminatorow_${notification.from.toUpperCase()}_${formatDate(
                      new Date(notification.sentDate),
                    )}.csv`,
                  );
                }}
              >
                Pobierz raport
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.ExaminerImportFailed:
        return (
          <>
            <div>Import egzaminatorów z {notification.from.toUpperCase()}:</div>
            <div>{`Błąd pobrania danych dla ${getCommitteeLabel(Number(notification.content))}`}</div>
          </>
        );
      case NotificationTypeId.ExaminerBlocked:
        return (
          <>
            <div>Blokada egzaminatora:</div>
            <div>{notification.content}</div>
          </>
        );
      case NotificationTypeId.ExportResultSuccess:
        return (
          <>
            <div>Eksport wyników do SIOEPKZ dla arkusza</div>
            <div>{parsedNotificationContent.SheetCode}</div>
            <div>został zakończony</div>
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  downloadSioePkzExportReport(
                    parsedNotificationContent.ExportResultStateId,
                    `Eksport_wyniki_SIOEPKZ_${formatDate(new Date(notification.sentDate))}.csv`,
                  );
                }}
              >
                Pobierz raport
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.ExportResultFailed:
        return (
          <>
            <div>Eksport wyników do SIOEPKZ dla arkusza</div>
            <div>{parsedNotificationContent.SheetCode}</div>
            <div>nie powiódł się.</div>
            <div>Spróbuj ponownie później.</div>
          </>
        );
      case NotificationTypeId.ReportFinished:
        return (
          <>
            <div>Zakończono generowanie dokumentacji egzaminacyjnej dla arkusza</div>
            <div>{parsedNotificationContent.SheetCode}</div>
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  handleDownloadExamResultsReport();
                }}
              >
                Pobierz dokumentację
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.ReportFailed:
        return (
          <>
            <div>Wystąpił błąd podczas generowania dokumentacji egzaminacyjnej dla arkusza</div>
            <div>{parsedNotificationContent.SheetCode}</div>
            <div>Spróbuj ponownie później</div>
          </>
        );
      case NotificationTypeId.ProcedureMessage:
        return (
          <>
            <div>{notification.content}</div>
          </>
        );
      case NotificationTypeId.ExamineeImportSuccess:
        return (
          <>
            {parsedNotificationContent.ExamineesStoredCount === 0 ? (
              <>
                Wszyscy zdający znajdują się już w systemie {t('global:shortNames.system')}. Nie zaimportowano nowych
                zdających.
              </>
            ) : (
              <>
                <div>
                  Zakończono import zdających z
                  <span> {systemType !== SystemId.Custom ? getSystemName() : <span>pliku</span>} </span> dla arkusza
                  <span> {parsedNotificationContent.SheetCode}</span>
                </div>
                <div>Zaimportowano {parsedNotificationContent.ExamineesStoredCount} </div>
                <Link href={getExamineeListUrl(parsedNotificationContent.SheetCode)}>Przejdź do wykazu zdających</Link>
              </>
            )}
          </>
        );
      case NotificationTypeId.ExamineeImportSomeRecordsNotPassValidation:
        return (
          <>
            <div>
              Zakończono import zdających z
              <span> {systemType !== SystemId.Custom ? getSystemName() : <span>pliku</span>} </span> dla arkusza
              <span> {parsedNotificationContent.SheetCode}</span>
            </div>
            <div>Zaimportowano {parsedNotificationContent.ExamineesStoredCount} </div>
            <Link href={`${Paths.examinees}/${parsedNotificationContent.SheetCode}`}>Przejdź do wykazu zdających</Link>
            <div>
              <div>Zdający dla których wystąpił błąd:</div>
              <Link
                component="button"
                variant="body2"
                onClick={async () => {
                  await downloadExamineeImportReport(
                    parsedNotificationContent.ImportExamineeStateId,
                    `Import_zdajacych_${parsedNotificationContent.SheetCode}_${formatDate(
                      new Date(notification.sentDate),
                    )}.csv`,
                  );
                }}
              >
                Pobierz raport
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.ExamineeImportFailed:
        return (
          <>
            <div>
              Import zdających z <span>{systemType !== SystemId.Custom ? getSystemName() : <span>pliku</span>} </span>
              dla arkusza <span> {parsedNotificationContent.SheetCode} </span>
              zakończył się z błędem, spróbuj ponownie później.
            </div>
          </>
        );
      case NotificationTypeId.ExamineeImportNoDataFromExternalSystem:
        return (
          <>
            <div>
              Import zdających z <span>{systemType !== SystemId.Custom ? getSystemName() : <span>pliku</span>} </span>
              dla arkusza <span> {parsedNotificationContent.SheetCode} </span>
              zakończył się z błędem - system zewnętrzny nie zwrócił danych.
            </div>
          </>
        );
      case NotificationTypeId.ExamSheetDeleteProcessSuccess:
        return (
          <>
            <div>
              Arkusz {parsedNotificationContent.SheetCode}
              <span> został poprawnie usunięty </span>
            </div>
          </>
        );
      case NotificationTypeId.ExamSheetDeleteProcessFailed:
        return (
          <>
            <div>
              Wystąpił błąd podczas usuwania arkusza {parsedNotificationContent.SheetCode}. Spróbuj ponownie później.
            </div>
          </>
        );
      case NotificationTypeId.DeleteTasksSolutionSheetsCompletedWithIssues:
        return (
          <>
            <div>Zakończono zlecenie usuwania kart.</div>
            <div>Poprawnie usunięto {parsedNotificationContent.TaskSolutionSheetsCount} kart zdających. Karty,</div>
            <div>dla których wystąpił problem:</div>
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  downloadDeleteTasksSolutionSheetReport(
                    parsedNotificationContent.CorrelationId,
                    `usuwanieKart_${format(new Date(notification.sentDate), 'ddMMyyHHmm')}.csv`,
                  );
                }}
              >
                Pobierz raport
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.DeleteTasksSolutionSheetsCompletedWithoutIssues:
        return (
          <>
            <div>Poprawnie usunięto {parsedNotificationContent.TaskSolutionSheetsCount} kart zdających.</div>
          </>
        );
      case NotificationTypeId.DeleteTasksSolutionSheetsInProgress:
        return (
          <>
            <div>Rozpoczęto usuwanie {parsedNotificationContent.TaskSolutionSheetsCount} kart zdających.</div>
          </>
        );
      case NotificationTypeId.UserDeleteFinished:
        return (
          <>
            <div>
              {handleDeleteUserResult(parsedNotificationContent.Succeeded)} {parsedNotificationContent.FirstName[0]}.{parsedNotificationContent.LastName}.
            </div>
            {!parsedNotificationContent.Succeeded && <div>Wystąpił błąd, spróbuj ponownie później.</div>}
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  history.push(Paths.users);
                }}
              >
                Przejdź do listy użytkowników.
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.CopySessionInProgress:
        return (
          <>
            <div>Rozpoczęto kopiowanie arkusza {parsedNotificationContent.SheetCode}.</div>
          </>
        );
      case NotificationTypeId.CopySessionSuccess:
        return (
          <>
            <div>Poprawnie skopiowano arkusz {parsedNotificationContent.SheetCode}.</div>
            <div>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  history.push(`${Paths.sessions}/${parsedNotificationContent.SessionId}`);
                }}
              >
                Przejdź do szczegółów sesji.
              </Link>
            </div>
          </>
        );
      case NotificationTypeId.CopySessionFailed:
        return (
          <>
            <div>Wystąpił błąd podczas kopiowania arkusza {parsedNotificationContent.SheetCode}.</div>
          </>
        );
      default:
        return (
          <>
            <div>{notification.content}</div>
          </>
        );
    }
  };

  return (
    <>
      <TooltipOne title="Powiadomienia" placement="top">
        <IconButton disableRipple onClick={handleToggleNotificationsList} aria-label="Powiadomienia">
          <IconWithDot dotHidden={!anyUnreadNotificationsFound}>
            <NotificationsNoneIcon fill={fill} className={navBarStyles.navIcon} />
          </IconWithDot>
        </IconButton>
      </TooltipOne>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          onScroll: getOnInfiniteScroll(handleOnScroll),
        }}
      >
        <div className={classes.listPanel}>
          <div>
            <Typography className={classes.title}>Powiadomienia</Typography>
          </div>
          <div>
            {notifications.length ? (
              <List>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    alignItems="flex-start"
                    className={classes.notification}
                    onClick={() => handleNotificationClick(notification.notificationType)}
                    onMouseEnter={() => setActiveNotificationId(notification.id)}
                    onMouseLeave={() => setActiveNotificationId(null)}
                    style={{ cursor: activeNotificationId === notification.id ? 'pointer' : undefined }}
                  >
                    {activeNotificationId === notification.id && (
                      <IconButton
                        disableRipple
                        size="small"
                        onClick={(event) => handleDeleteNotification(event, notification.id)}
                        style={{ position: 'absolute', top: 0, right: 0 }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}

                    <ListItemAvatar>
                      <Avatar
                        style={notification.isRead ? undefined : { color: '#3560FF', backgroundColor: '#3560FF1A' }}
                      >
                        {getNotificationIcon(notification.notificationType)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <div className={notification.isRead ? undefined : classes.notRead}>
                          {getNotificationText(notification)}
                        </div>
                      }
                      secondary={`${timeAgo(notification.sentDate)} temu`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <div style={{ margin: '18px' }}>
                <InfoShield
                  icon={<NotificationsNoneIcon style={{ color: '#BCBEDC', fontSize: '60px' }} />}
                  message={
                    <>
                      <div>W tej chwili nie posiadasz</div>
                      <div>żadnych powiadomień</div>
                    </>
                  }
                  backgroundColor="#EBECF4"
                  fontSize="15px"
                />
              </div>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default NotificationsMenu;
